import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";

import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import { GET_STUDENT_LIST } from "../graphql/query";
import themestyle from "../theme";
import type {
  GetStudentListResponse,
  StudentListItem,
} from "../interfaces/student.interface";
import DataGrids from "./DataGrid";
import { ASSIGN_TYPE_OPTIONS } from "../utils/const";

const STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  PENDING_APPROVAL: { label: "Pending Approval", bg: "#FFF3E0", fg: "#E65100" },
  ACTIVE: { label: "Active", bg: "#E7F4E4", fg: "#2E7D32" },
  INACTIVE: { label: "Inactive", bg: "#F1F1F1", fg: "#616161" },
  ARCHIVED: { label: "Archived", bg: "#FFF4E5", fg: "#B26A00" },
  DELETED: { label: "Deleted", bg: "#FDECEA", fg: "#C62828" },
};

// Single source of truth for status-driven capabilities, shared by both the
// Status column (on/off toggle) and the Actions column (delete vs restore).
// "Live" accounts (ACTIVE/INACTIVE) can be toggled & deleted; every other
// state (ARCHIVED/DELETED) is recoverable via the Restore action. Deriving
// both columns from this prevents the predicates from drifting apart.
const isLiveStatus = (status: string): boolean =>
  status === "ACTIVE" || status === "INACTIVE" || status === "PENDING_APPROVAL";

const resolveStatus = (row: { accountStatus?: string; isActive?: boolean }): string =>
  row.accountStatus || (row.isActive ? "ACTIVE" : "INACTIVE");

type AllStudentTableProps = {
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string, name: string, assignedClasses?: string[]) => void;
  onStatusToggle?: (id: string, name: string, currentStatus: boolean) => void;
  onRestore?: (id: string, name: string) => void;
  refreshTrigger?: number;
};

const AllStudentTable: React.FC<AllStudentTableProps> = ({
  onView,
  onEdit,
  onDelete,
  onStatusToggle,
  onRestore,
  refreshTrigger,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const { data, loading, refetch, previousData } =
    useQuery<GetStudentListResponse>(GET_STUDENT_LIST, {
      variables: {
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search: searchQuery || "",
      },
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    if (refreshTrigger) refetch();
  }, [refreshTrigger, refetch]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, pageSize: 5, page: 0 }));
  }, [isMobile]);

  const rows = useMemo(() => {
    const dataList: StudentListItem[] = data?.getStudentList?.data ?? [];
    return dataList
      .filter((row) => !!row)
      .map((row, index) => {
        const serial =
          paginationModel.page * paginationModel.pageSize + index + 1;
        return {
          ...row,
          serial: serial.toString().padStart(2, "0"),
        };
      });
  }, [data, paginationModel.page, paginationModel.pageSize]);

  const rowCount =
    data?.getStudentList?.totalCount ??
    previousData?.getStudentList?.totalCount ??
    0;

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "serial",
        headerName: "S.No",
        minWidth: 50,
        flex: 0.3,
        sortable: false,
      },
      {
        field: "studentId",
        headerName: "Student Id",
        flex: 0.7,
        minWidth: 100,
      },
      {
        field: "fullName",
        headerName: "Full Name",
        flex: 0.9,
        minWidth: 130,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.1,
        minWidth: 150,
      },
      {
        field: "assignType",
        headerName: "Pass Type",
        flex: 0.9,
        minWidth: 130,
        valueGetter: (params: any) => {
          const pass = ASSIGN_TYPE_OPTIONS.find(
            (opt) => opt.value === params.row.assignType,
          );
          return pass ? pass.label : params.row.assignType || "-";
        },
      },
      {
        field: "assignToClass",
        headerName: "Assigned class",
        flex: 0.9,
        minWidth: 130,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: any) => {
          const classes = params.row.assignToClass;
          if (!classes || classes.length === 0) return "-";
          return classes.join(", ");
        },
      },
      {
        field: "phoneNumber",
        headerName: "Phone Number",
        flex: 0.7,
        minWidth: 120,
      },
      {
        field: "isActive",
        headerName: "Status",
        flex: 1,
        minWidth: 190,
        sortable: false,
        renderCell: (params) => {
          const status: string = resolveStatus(params.row);
          const meta = STATUS_META[status] || STATUS_META.INACTIVE;
          // Quick on/off toggle only makes sense for live (ACTIVE/INACTIVE/PENDING)
          // accounts; archived/deleted are restored via the Restore action.
          const canToggle = isLiveStatus(status);
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                width: "100%",
              }}
            >
              <Chip
                label={meta.label}
                size="small"
                sx={{
                  bgcolor: meta.bg,
                  color: meta.fg,
                  fontWeight: 600,
                  // Let a long label (e.g. "Pending Approval") wrap to two lines and
                  // shrink instead of pushing the toggle out of the cell.
                  height: "auto",
                  minWidth: 0,
                  flexShrink: 1,
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    lineHeight: 1.2,
                    py: "3px",
                    textAlign: "center",
                  },
                }}
              />
              {canToggle && (
                <Switch
                  checked={status === "ACTIVE"}
                  onChange={() =>
                    onStatusToggle?.(
                      params.row.id,
                      params.row.fullName,
                      status === "ACTIVE",
                    )
                  }
                  size="small"
                  sx={{
                    // Pin the toggle so it's always visible (never clipped by a long chip).
                    flexShrink: 0,
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#9BCB4A" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#9BCB4A",
                    },
                    "& .MuiSwitch-track": { backgroundColor: "#D4D8DD" },
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 120,
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const status = resolveStatus(params.row);
          // Live (ACTIVE/INACTIVE) accounts can be deleted; archived/deleted
          // accounts are recoverable via Restore. Same predicate as the Status
          // column's toggle, so the two can never drift out of sync.
          const showDelete = isLiveStatus(status);
          const showRestore = !isLiveStatus(status);

          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                size="small"
                title="Student Report"
                onClick={() => navigate(`/students/report/${params.row.id}`)}
                sx={{
                  border: "1px solid #C7D9F5",
                  color: "#1565C0",
                  width: 28,
                  height: 28,
                }}
              >
                <AssessmentOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                title="View Student"
                onClick={() => onView(params.row.id)}
                sx={{
                  border: "1px solid #D1E7C7",
                  color: themestyle.colors.buttonGreen,
                  width: 28,
                  height: 28,
                }}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
              {showDelete && (
                <IconButton
                  size="small"
                  title="Delete Student"
                  onClick={() =>
                    onDelete(
                      params.row.id,
                      params.row.fullName,
                      params.row.assignToClass,
                    )
                  }
                  sx={{
                    border: "1px solid #F2B6B6",
                    color: "#E53935",
                    width: 28,
                    height: 28,
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
              {showRestore && (
                <IconButton
                  size="small"
                  title="Restore Student"
                  onClick={() => onRestore?.(params.row.id, params.row.fullName)}
                  sx={{
                    border: "1px solid #C7D9F5",
                    color: "#1565C0",
                    width: 28,
                    height: 28,
                  }}
                >
                  <SettingsBackupRestoreIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        },
      },
    ],
    [onView, onDelete, onStatusToggle, onRestore, navigate],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: "#F7FAF3",
        borderRadius: 3,
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          maxWidth: "100%",
        }}
      >
        <DataGrids
          rows={rows}
          columns={columns}
          toolbar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          toolbarShowAddIcon={false}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          getRowId={(row: any) => row.id}
          onMobileView={(row: any) => onView(row.id)}
          onMobileDelete={(row: any) =>
            onDelete(row.id, row.fullName, row.assignToClass)
          }
          onMobileReport={(row: any) =>
            navigate(`/students/report/${row.id}`)
          }
          onMobileStatusToggle={(row: any) =>
            // Derive from resolveStatus (accountStatus-authoritative) like the desktop
            // toggle, so mobile can't disagree with the backend on ARCHIVED/DELETED rows.
            onStatusToggle?.(row.id, row.fullName, resolveStatus(row) === "ACTIVE")
          }
          loading={loading}
          sx={{
            height: { xs: "auto", sm: "60vh", md: "60vh" },
            minHeight: 320,
            width: "100%",
            maxWidth: "100%",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F9FAFB",
              borderBottom: "1px solid #E5E7EB",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#F7FBF5",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #EEF2F7",
              fontSize: "13px",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AllStudentTable;
